import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  MoreHorizontal,
  Tags,
  Trash2,
  Edit2,
  Copy,
  Palette,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/toaster';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { normalizeError, tagSetsApi, tagsApi } from '@/lib/api';
import { getContrastTextColor } from '@/lib/utils';
import { useMyCapabilities } from '@/hooks/use-permissions';
import type { TagSet, Tag } from '@/types';

const TAG_MANAGEMENT_CAPS = ['tag_sets.create', 'tag_sets.edit', 'tags.create', 'tags.edit', 'list.tag_sets.assign'];

const DEFAULT_TAG_COLORS = [
  '#ef4444', // red
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#06b6d4', // cyan
  '#3b82f6', // blue
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#6b7280', // gray
];

export function TagManagerPage() {
  const { data: capabilities } = useMyCapabilities();
  const canManageTags = TAG_MANAGEMENT_CAPS.some((key) => capabilities?.[key]);
  const canDeleteTagSets = Boolean(capabilities?.['tag_sets.delete']);
  const canDeleteTags = Boolean(capabilities?.['tags.delete']);
  const queryClient = useQueryClient();
  const [selectedTagSet, setSelectedTagSet] = useState<TagSet | null>(null);
  const [createTagSetOpen, setCreateTagSetOpen] = useState(false);
  const [editTagSet, setEditTagSet] = useState<TagSet | null>(null);
  const [deleteTagSet, setDeleteTagSet] = useState<TagSet | null>(null);
  const [createTagOpen, setCreateTagOpen] = useState(false);
  const [editTag, setEditTag] = useState<Tag | null>(null);
  const [deleteTag, setDeleteTag] = useState<Tag | null>(null);

  const { data: tagSets = [], isLoading } = useQuery({
    queryKey: ['tagSets'],
    queryFn: async () => {
      const response = await tagSetsApi.getAll();
      return response.data;
    },
  });

  const { data: selectedTagSetTags = [], isLoading: isLoadingTags } = useQuery({
    queryKey: ['tagSetTags', selectedTagSet?.id],
    queryFn: async () => {
      if (!selectedTagSet) return [];
      const response = await tagSetsApi.getTags(selectedTagSet.id);
      return response.data;
    },
    enabled: !!selectedTagSet,
  });

  const createTagSetMutation = useMutation({
    mutationFn: (data: { name: string; description?: string }) =>
      tagSetsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tagSets'] });
      setCreateTagSetOpen(false);
      toast({ title: 'Tag set created' });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Failed to create tag set',
        description: normalizeError(error).message,
        variant: 'destructive',
      });
    },
  });

  const updateTagSetMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; description?: string } }) =>
      tagSetsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tagSets'] });
      setEditTagSet(null);
      toast({ title: 'Tag set updated' });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Failed to update tag set',
        description: normalizeError(error).message,
        variant: 'destructive',
      });
    },
  });

  const deleteTagSetMutation = useMutation({
    mutationFn: (id: string) => tagSetsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tagSets'] });
      if (selectedTagSet?.id === deleteTagSet?.id) {
        setSelectedTagSet(null);
      }
      setDeleteTagSet(null);
      toast({ title: 'Tag set deleted' });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Failed to delete tag set',
        description: normalizeError(error).message,
        variant: 'destructive',
      });
    },
  });

  const duplicateTagSetMutation = useMutation({
    mutationFn: (id: string) => tagSetsApi.duplicate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tagSets'] });
      toast({ title: 'Tag set duplicated' });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Failed to duplicate tag set',
        description: normalizeError(error).message,
        variant: 'destructive',
      });
    },
  });

  const createTagMutation = useMutation({
    mutationFn: ({ tagSetId, data }: { tagSetId: string; data: { name: string; color?: string } }) =>
      tagSetsApi.createTag(tagSetId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tagSetTags', selectedTagSet?.id] });
      queryClient.invalidateQueries({ queryKey: ['tagSets'] });
      setCreateTagOpen(false);
      toast({ title: 'Tag created' });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Failed to create tag',
        description: normalizeError(error).message,
        variant: 'destructive',
      });
    },
  });

  const updateTagMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; color?: string } }) =>
      tagsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tagSetTags', selectedTagSet?.id] });
      setEditTag(null);
      toast({ title: 'Tag updated' });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Failed to update tag',
        description: normalizeError(error).message,
        variant: 'destructive',
      });
    },
  });

  const deleteTagMutation = useMutation({
    mutationFn: (id: string) => tagsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tagSetTags', selectedTagSet?.id] });
      queryClient.invalidateQueries({ queryKey: ['tagSets'] });
      setDeleteTag(null);
      toast({ title: 'Tag deleted' });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Failed to delete tag',
        description: normalizeError(error).message,
        variant: 'destructive',
      });
    },
  });

  if (capabilities !== undefined && !canManageTags) {
    return (
      <div className="container max-w-6xl py-6 flex flex-col items-center justify-center gap-4">
        <Tags className="h-12 w-12 text-muted-foreground/50" />
        <h2 className="text-lg font-semibold">No permission</h2>
        <p className="text-muted-foreground text-center max-w-md">
          You do not have permission to manage tags or tag sets. Contact an administrator if you need access.
        </p>
      </div>
    );
  }

  return (
    <div className="container max-w-6xl py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Tag Manager</h1>
          <p className="text-muted-foreground">
            Create and manage tag sets for organizing tasks
          </p>
        </div>
        {capabilities?.['tag_sets.create'] && (
          <Button onClick={() => setCreateTagSetOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Tag Set
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Tag Sets List */}
        <Card className="flex flex-col max-h-[calc(100vh-200px)]">
          <CardHeader className="flex-shrink-0">
            <CardTitle className="flex items-center gap-2">
              <Tags className="h-5 w-5" />
              Tag Sets
            </CardTitle>
            <CardDescription>
              {tagSets.length} tag set{tagSets.length !== 1 ? 's' : ''} available
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1 min-h-0 overflow-hidden">
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : tagSets.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Tags className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No tag sets yet</p>
                <p className="text-sm">Create a tag set to start organizing tasks.</p>
              </div>
            ) : (
              <ScrollArea className="h-full pr-4">
                <div className="space-y-2">
                  {tagSets.map((tagSet) => (
                    <div
                      key={tagSet.id}
                      className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-colors ${
                        selectedTagSet?.id === tagSet.id
                          ? 'bg-primary/10 border-primary'
                          : 'hover:bg-muted/50'
                      }`}
                      onClick={() => setSelectedTagSet(tagSet)}
                    >
                      <div className="min-w-0">
                        <p className="font-medium truncate">{tagSet.name}</p>
                        {tagSet.description && (
                          <p className="text-sm text-muted-foreground truncate max-w-[200px]">
                            {tagSet.description}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {tagSet._count?.tags || 0} tag{(tagSet._count?.tags || 0) !== 1 ? 's' : ''} ·{' '}
                          {tagSet._count?.listTags || 0} list{(tagSet._count?.listTags || 0) !== 1 ? 's' : ''}
                        </p>
                      </div>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditTagSet(tagSet);
                            }}
                          >
                            <Edit2 className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              duplicateTagSetMutation.mutate(tagSet.id);
                            }}
                          >
                            <Copy className="h-4 w-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          {canDeleteTagSets && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteTagSet(tagSet);
                                }}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        {/* Tags in Selected Set */}
        <Card className="flex flex-col max-h-[calc(100vh-200px)]">
          <CardHeader className="flex-shrink-0">
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              {selectedTagSet ? selectedTagSet.name : 'Tags'}
            </CardTitle>
            <CardDescription>
              {selectedTagSet
                ? `${selectedTagSetTags.length} tag${selectedTagSetTags.length !== 1 ? 's' : ''} in this set`
                : 'Select a tag set to view its tags'}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1 min-h-0 overflow-hidden flex flex-col">
            {!selectedTagSet ? (
              <div className="text-center py-8 text-muted-foreground">
                <Tags className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Select a tag set</p>
                <p className="text-sm">Click on a tag set to view and manage its tags.</p>
              </div>
            ) : isLoadingTags ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mb-4 flex-shrink-0"
                  onClick={() => setCreateTagOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Tag
                </Button>
                {selectedTagSetTags.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Palette className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No tags yet</p>
                    <p className="text-sm">Add tags to this set.</p>
                  </div>
                ) : (
                  <ScrollArea className="flex-1 min-h-0 pr-4">
                    <div className="space-y-2">
                      {selectedTagSetTags.map((tag) => {
                        const bgColor = tag.color || '#6b7280';
                        const textColor = getContrastTextColor(bgColor);
                        return (
                        <div
                          key={tag.id}
                          className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50"
                        >
                          <Badge
                            variant="secondary"
                            style={{
                              backgroundColor: bgColor,
                              color: textColor,
                              borderColor: 'transparent',
                            }}
                            className="font-medium"
                          >
                            {tag.name}
                          </Badge>

                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => setEditTag(tag)}
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                            </Button>
                            {canDeleteTags && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-destructive hover:text-destructive"
                                onClick={() => setDeleteTag(tag)}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Tag Set Dialog */}
      <CreateTagSetDialog
        open={createTagSetOpen}
        onOpenChange={setCreateTagSetOpen}
        onSubmit={(data) => createTagSetMutation.mutate(data)}
        isLoading={createTagSetMutation.isPending}
      />

      {/* Edit Tag Set Dialog */}
      <EditTagSetDialog
        tagSet={editTagSet}
        onOpenChange={(open) => !open && setEditTagSet(null)}
        onSubmit={(data) =>
          editTagSet && updateTagSetMutation.mutate({ id: editTagSet.id, data })
        }
        isLoading={updateTagSetMutation.isPending}
      />

      {/* Delete Tag Set Confirmation */}
      <AlertDialog open={!!deleteTagSet} onOpenChange={(open) => !open && setDeleteTagSet(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete tag set?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deleteTagSet?.name}" and all its tags.
              {(deleteTagSet?._count?.listTags || 0) > 0 && (
                <span className="block mt-2 text-destructive">
                  Warning: This tag set is assigned to {deleteTagSet?._count?.listTags} list(s).
                  You must remove it from all lists first.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteTagSet && deleteTagSetMutation.mutate(deleteTagSet.id)}
              disabled={(deleteTagSet?._count?.listTags || 0) > 0}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Tag Dialog */}
      <CreateTagDialog
        open={createTagOpen}
        onOpenChange={setCreateTagOpen}
        onSubmit={(data) =>
          selectedTagSet && createTagMutation.mutate({ tagSetId: selectedTagSet.id, data })
        }
        isLoading={createTagMutation.isPending}
      />

      {/* Edit Tag Dialog */}
      <EditTagDialog
        tag={editTag}
        onOpenChange={(open) => !open && setEditTag(null)}
        onSubmit={(data) => editTag && updateTagMutation.mutate({ id: editTag.id, data })}
        isLoading={updateTagMutation.isPending}
      />

      {/* Delete Tag Confirmation */}
      <AlertDialog open={!!deleteTag} onOpenChange={(open) => !open && setDeleteTag(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete tag?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the tag "{deleteTag?.name}" and remove it from all tasks.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteTag && deleteTagMutation.mutate(deleteTag.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Create Tag Set Dialog
function CreateTagSetDialog({
  open,
  onOpenChange,
  onSubmit,
  isLoading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: { name: string; description?: string }) => void;
  isLoading: boolean;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({ name: name.trim(), description: description.trim() || undefined });
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setName('');
      setDescription('');
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Tag Set</DialogTitle>
          <DialogDescription>
            Create a new tag set to organize related tags.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Priority, Status, Department"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What is this tag set for?"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || isLoading}>
              {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Edit Tag Set Dialog
function EditTagSetDialog({
  tagSet,
  onOpenChange,
  onSubmit,
  isLoading,
}: {
  tagSet: TagSet | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: { name?: string; description?: string }) => void;
  isLoading: boolean;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  useState(() => {
    if (tagSet) {
      setName(tagSet.name);
      setDescription(tagSet.description || '');
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({ name: name.trim(), description: description.trim() || undefined });
  };

  // Reset form when tagSet changes
  if (tagSet && name !== tagSet.name && description !== (tagSet.description || '')) {
    setName(tagSet.name);
    setDescription(tagSet.description || '');
  }

  return (
    <Dialog open={!!tagSet} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Tag Set</DialogTitle>
          <DialogDescription>Update the tag set name and description.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Priority, Status, Department"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description (optional)</Label>
              <Textarea
                id="edit-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What is this tag set for?"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || isLoading}>
              {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Create Tag Dialog
function CreateTagDialog({
  open,
  onOpenChange,
  onSubmit,
  isLoading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: { name: string; color?: string }) => void;
  isLoading: boolean;
}) {
  const [name, setName] = useState('');
  const [color, setColor] = useState(DEFAULT_TAG_COLORS[0]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({ name: name.trim(), color });
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setName('');
      setColor(DEFAULT_TAG_COLORS[0]);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Tag</DialogTitle>
          <DialogDescription>Add a new tag to this set.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="tag-name">Name</Label>
              <Input
                id="tag-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Urgent, In Review"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2">
                {DEFAULT_TAG_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`w-8 h-8 rounded-full border-2 transition-transform ${
                      color === c ? 'border-foreground scale-110' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: c }}
                    onClick={() => setColor(c)}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Label htmlFor="custom-color" className="text-sm">
                  Custom:
                </Label>
                <Input
                  id="custom-color"
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="w-12 h-8 p-0 border-0"
                />
                <span className="text-sm text-muted-foreground">{color}</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Preview</Label>
              <Badge
                variant="secondary"
                style={{
                  backgroundColor: `${color}20`,
                  color: color,
                  borderColor: color,
                }}
                className="border"
              >
                {name || 'Tag Preview'}
              </Badge>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || isLoading}>
              {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Edit Tag Dialog
function EditTagDialog({
  tag,
  onOpenChange,
  onSubmit,
  isLoading,
}: {
  tag: Tag | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: { name?: string; color?: string }) => void;
  isLoading: boolean;
}) {
  const [name, setName] = useState('');
  const [color, setColor] = useState(DEFAULT_TAG_COLORS[0]);

  // Reset form when tag changes
  if (tag && (name !== tag.name || color !== tag.color)) {
    setName(tag.name);
    setColor(tag.color);
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({ name: name.trim(), color });
  };

  return (
    <Dialog open={!!tag} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Tag</DialogTitle>
          <DialogDescription>Update the tag name and color.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-tag-name">Name</Label>
              <Input
                id="edit-tag-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Urgent, In Review"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2">
                {DEFAULT_TAG_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`w-8 h-8 rounded-full border-2 transition-transform ${
                      color === c ? 'border-foreground scale-110' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: c }}
                    onClick={() => setColor(c)}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Label htmlFor="edit-custom-color" className="text-sm">
                  Custom:
                </Label>
                <Input
                  id="edit-custom-color"
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="w-12 h-8 p-0 border-0"
                />
                <span className="text-sm text-muted-foreground">{color}</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Preview</Label>
              <Badge
                variant="secondary"
                style={{
                  backgroundColor: `${color}20`,
                  color: color,
                  borderColor: color,
                }}
                className="border"
              >
                {name || 'Tag Preview'}
              </Badge>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || isLoading}>
              {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
